// Copyright 2026 Monterosa Productions Limited. All rights reserved.

import { UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts';
import { config } from './config.js';

const settings: UserManagerSettings = {
  authority:     config.oktaAuthority,
  client_id:     config.oktaClientId,
  redirect_uri:  config.redirectUri,
  response_type: 'code',
  // offline_access requests a refresh token so signinSilent() uses the refresh_token grant
  // instead of a hidden iframe — required for Safari (ITP), Firefox (ETP), and future Chrome.
  scope:         'openid profile email offline_access',
  ...(config.oktaClientSecret ? { client_secret: config.oktaClientSecret } : {}),
  // Refresh is handled on-demand (via requestLogin from embed or token expiry event).
  automaticSilentRenew: false,
  // Reduce iframe-based silent renew timeout so a failed silent refresh falls through
  // to the full login redirect faster (default is 10s).
  silentRequestTimeoutInSeconds: 5,
  // stateStore must use localStorage — sessionStorage is wiped by some browsers during the
  // external Okta redirect, causing code_verifier mismatch and invalid_client on callback.
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
  // localStorage so the Okta user (+ refresh token) survives across tabs and page reloads.
  userStore:  new WebStorageStateStore({ store: window.localStorage }),
};

export const userManager = new UserManager(settings);

export async function login(): Promise<void> {
  await userManager.signinRedirect();
}

export async function handleCallback(): Promise<void> {
  await userManager.signinRedirectCallback();
}

export async function getAccessToken(): Promise<string | null> {
  const user = await userManager.getUser();
  if (!user || user.expired) return null;
  // The access token (not the id_token) is exchanged for the internal Proxima token.
  // Its `uid` claim carries the Okta user ID and is configured as the userIdClaim downstream.
  return user.access_token ?? null;
}

let _refreshInFlight: Promise<string | null> | null = null;

export async function silentRefresh(): Promise<string | null> {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = userManager.signinSilent()
    .then(u => u?.access_token ?? null)
    .catch(() => null)
    .finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
}
